import { useState, useEffect, useCallback, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { chatId } from '~/lib/persistence/useChatHistory';
import { apiListStorageFiles, apiUploadStorageFile, apiDeleteStorageFile, type StorageFile } from '~/lib/api/storage';
import { classNames } from '~/utils/classNames';
import { ConfirmationDialog } from '~/components/ui/Dialog';
import { BREAKPOINTS, useIsBelow } from '~/lib/hooks/useMediaQuery';

function formatBytes(bytes: number): string {
  if (bytes === 0) {
    return '0 B';
  }

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function getFileIcon(contentType: string): string {
  if (contentType.startsWith('image/')) {
    return 'i-ph:image';
  }

  if (contentType.startsWith('video/')) {
    return 'i-ph:video';
  }

  if (contentType.startsWith('audio/')) {
    return 'i-ph:music-note';
  }

  if (contentType === 'application/pdf') {
    return 'i-ph:file-pdf';
  }

  if (contentType.includes('json')) {
    return 'i-ph:file-code';
  }

  if (contentType.includes('text')) {
    return 'i-ph:file-text';
  }

  return 'i-ph:file';
}

export function AssetsPanel() {
  const currentChatId = useStore(chatId);
  const [files, setFiles] = useState<StorageFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<StorageFile | null>(null);
  const [fileToDelete, setFileToDelete] = useState<StorageFile | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [search, setSearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Below md the 220px list + detail split left ~130px for the detail — show one at a time.
  const isNarrow = useIsBelow(BREAKPOINTS.md);

  const loadFiles = useCallback(async (chatId: string) => {
    try {
      const fileList = await apiListStorageFiles(chatId);
      setFiles(fileList);
    } catch {
      setFiles([]);
    }
  }, []);

  useEffect(() => {
    if (!currentChatId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    loadFiles(currentChatId).finally(() => setLoading(false));
  }, [currentChatId, loadFiles]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file || !currentChatId) {
      return;
    }

    setUploading(true);

    try {
      await apiUploadStorageFile(currentChatId, file);
      await loadFiles(currentChatId);
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (file: StorageFile) => {
    if (!currentChatId) {
      return;
    }

    try {
      await apiDeleteStorageFile(currentChatId, file.key);
      await loadFiles(currentChatId);

      if (selectedFile?.key === file.key) {
        setSelectedFile(null);
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleSelect = (file: StorageFile) => {
    setSelectedFile(file);
    setCopySuccess(false);
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = (file: StorageFile) => {
    window.open(file.url, '_blank');
  };

  const filteredFiles = search ? files.filter((f) => f.name.toLowerCase().includes(search.toLowerCase())) : files;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="i-svg-spinners:90-ring-with-bg text-xl text-accent" />
      </div>
    );
  }

  if (!currentChatId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <div className="i-ph:image text-3xl text-muted mb-3" />
        <h4 className="text-[13px] text-primary mb-1 font-geist">No chat selected</h4>
        <p className="text-[11.5px] text-muted max-w-[240px] leading-relaxed">
          Start a chat to upload and manage assets for your landing page.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb / actions bar */}
      <div className="flex items-center gap-2 bg-surface border-b border-hairline px-3 py-2 text-[11.5px] text-muted">
        <span className="font-geist-mono">~/assets</span>
        {selectedFile && (
          <>
            <div className="i-ph:caret-right text-[10px]" />
            <span className="font-geist-mono text-primary truncate">{selectedFile.name}</span>
          </>
        )}
        <div className="flex-1" />
        <label className={classNames('ui-chip', uploading && 'opacity-50 cursor-not-allowed')}>
          {uploading ? (
            <div className="i-svg-spinners:90-ring-with-bg text-[11px]" />
          ) : (
            <div className="i-ph:upload-simple text-[11px]" />
          )}
          Upload
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      </div>

      {/* Body: 2-column grid */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-[220px_1fr] min-h-0">
        {/* LEFT — file list */}
        <div
          className={classNames(
            'bg-surface md:border-r border-hairline overflow-y-auto py-2',
            isNarrow && selectedFile && 'hidden',
          )}
        >
          <div className="px-3 pb-2 flex items-center gap-1.5 sticky top-0 bg-surface">
            <div className="i-ph:magnifying-glass text-[11px] text-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find file"
              className="flex-1 bg-transparent border-0 outline-none text-[11.5px] text-primary placeholder:text-faint font-geist-mono"
            />
          </div>
          {filteredFiles.length === 0 ? (
            <div className="px-3 py-6 text-center">
              <div className="i-ph:folder-dashed text-2xl text-faint mx-auto mb-2" />
              <p className="text-[11.5px] text-muted">{search ? 'No matches' : 'No files uploaded'}</p>
            </div>
          ) : (
            filteredFiles.map((file) => {
              const isSel = selectedFile?.key === file.key;
              return (
                <button
                  key={file.key}
                  onClick={() => handleSelect(file)}
                  className={classNames(
                    'w-full flex items-center gap-1.5 px-3 py-1 touch:py-2.5 text-left font-geist-mono text-[11.5px] touch:text-[13px] border-0 cursor-pointer transition-colors',
                    isSel ? 'bg-sunken text-primary' : 'bg-transparent text-secondary hover:text-primary',
                  )}
                >
                  <div className={classNames(getFileIcon(file.contentType), 'text-[12px] text-muted flex-shrink-0')} />
                  <span className="truncate">{file.name}</span>
                </button>
              );
            })
          )}
        </div>

        {/* RIGHT — preview */}
        <div
          className={classNames('bg-page overflow-auto flex flex-col min-h-0', isNarrow && !selectedFile && 'hidden')}
        >
          {!selectedFile ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="i-ph:cursor-click text-2xl text-faint mb-2" />
              <p className="text-[11.5px] text-muted">Select a file to view details</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 border-b border-hairline">
                <div className="flex items-center gap-2 min-w-0">
                  {isNarrow && (
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      className="ui-btn-icon flex-shrink-0 -ml-1"
                      aria-label="Back to files"
                    >
                      <div className="i-ph:caret-left text-[14px]" />
                    </button>
                  )}
                  <div
                    className={classNames(getFileIcon(selectedFile.contentType), 'text-base text-accent flex-shrink-0')}
                  />
                  <div className="min-w-0">
                    <div className="text-[12px] text-primary truncate font-geist">{selectedFile.name}</div>
                    <div className="text-[10.5px] text-muted">
                      {selectedFile.contentType} · {formatBytes(selectedFile.size)} ·{' '}
                      {formatDate(selectedFile.lastModified)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => handleCopyUrl(selectedFile.url)}
                    className="ui-btn-ghost"
                    aria-label={copySuccess ? 'Copied' : 'Copy URL'}
                  >
                    <div className={copySuccess ? 'i-ph:check text-[11px]' : 'i-ph:copy text-[11px]'} />
                    <span className="hidden sm:inline">{copySuccess ? 'Copied' : 'Copy URL'}</span>
                  </button>
                  <button onClick={() => handleDownload(selectedFile)} className="ui-btn-ghost" aria-label="Download">
                    <div className="i-ph:download-simple text-[11px]" />
                    <span className="hidden sm:inline">Download</span>
                  </button>
                  <button
                    onClick={() => setFileToDelete(selectedFile)}
                    className="ui-btn-icon text-danger hover:text-danger"
                    title="Delete"
                    aria-label="Delete"
                  >
                    <div className="i-ph:trash text-[12px]" />
                  </button>
                </div>
              </div>

              <div className="px-4 py-2 border-b border-hairline bg-surface">
                <div className="ui-caption mb-1">Public URL</div>
                <code className="text-[11px] font-geist-mono text-accent block truncate">{selectedFile.url}</code>
              </div>

              <div className="flex-1 overflow-auto p-4 flex items-center justify-center">
                {selectedFile.contentType.startsWith('image/') ? (
                  <img
                    src={selectedFile.url}
                    alt={selectedFile.name}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                ) : (
                  <div className="text-center">
                    <div
                      className={classNames(getFileIcon(selectedFile.contentType), 'text-4xl text-faint mx-auto mb-2')}
                    />
                    <p className="text-[11.5px] text-muted">Preview not available</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmationDialog
        isOpen={fileToDelete !== null}
        onClose={() => setFileToDelete(null)}
        onConfirm={() => {
          const file = fileToDelete;
          setFileToDelete(null);

          if (file) {
            handleDelete(file);
          }
        }}
        title="Delete this file?"
        description={
          fileToDelete
            ? `"${fileToDelete.name}" will be permanently deleted. This can't be undone.`
            : "This can't be undone."
        }
        confirmLabel="Delete"
        variant="destructive"
      />
    </div>
  );
}
